<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import {
  Building2,
  Cake,
  ClipboardCheck,
  FileText,
  FolderKanban,
  GraduationCap,
  Network,
  Search,
  Siren,
  TreePalm,
  Users,
  Waypoints,
} from 'lucide-vue-next';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, type RouteLocationRaw } from 'vue-router';

import type {
  AssignmentListItemDto,
  DocumentDto,
  EmployeeListItemDto,
  ProjectListItemDto,
} from '@/api/generated/models/index.js';

import { assignmentsControllerListV1 } from '@/api/generated/assignments/assignments.js';
import { documentsControllerListV1 } from '@/api/generated/documents/documents.js';
import { employeesControllerListV1 } from '@/api/generated/employees/employees.js';
import { projectsControllerListV1 } from '@/api/generated/projects/projects.js';
import EmployeeAvatar from '@/components/EmployeeAvatar.vue';


/**
 * Командная палитра (⌘K / Ctrl+K).
 *
 * Единое поле поиска по людям, проектам, документам и разделам. Списки берутся
 * из тех же query-ключей, что и страницы, — открытие палитры переиспользует кэш.
 * Данные грузятся только когда палитра открыта (`enabled`).
 */
const open = defineModel<boolean>('open', { default: false });

const { t } = useI18n();
const router = useRouter();

const q = ref('');
const cursor = ref(0);
const inputEl = ref<HTMLInputElement | null>(null);
const listEl = ref<HTMLElement | null>(null);

const { data: employees } = useQuery({
  queryKey: ['employees', 'list'],
  queryFn: () => employeesControllerListV1() as unknown as Promise<EmployeeListItemDto[]>,
  enabled: open,
});
const { data: projects } = useQuery({
  queryKey: ['projects', 'list'],
  queryFn: () => projectsControllerListV1() as unknown as Promise<ProjectListItemDto[]>,
  enabled: open,
});
const { data: documents } = useQuery({
  queryKey: ['documents', 'list'],
  queryFn: () => documentsControllerListV1() as unknown as Promise<DocumentDto[]>,
  enabled: open,
});

// Поручения, где я участник: поиск по номеру («142», «№ 142») и тексту.
const { data: assignments } = useQuery({
  queryKey: ['assignments', 'mine', 'all'],
  queryFn: () => assignmentsControllerListV1({ view: 'mine', tab: 'all' }) as unknown as Promise<AssignmentListItemDto[]>,
  enabled: open,
});

interface PaletteItem {
  key: string;
  title: string;
  hint?: string;
  to: RouteLocationRaw;
  employee?: EmployeeListItemDto;
  icon?: unknown;
}
interface PaletteGroup {
  key: string;
  label: string;
  items: PaletteItem[];
}

/** Разделы — палитра работает и как навигация. */
const destinations = computed<PaletteItem[]>(() => [
  { key: 'd-assignments', title: t('nav.assignments'), to: { name: 'assignments' }, icon: ClipboardCheck },
  { key: 'd-employees', title: t('nav.employees'), to: { name: 'employees' }, icon: Users },
  { key: 'd-org', title: t('nav.orgChart'), to: { name: 'org-chart' }, icon: Network },
  { key: 'd-birthdays', title: t('nav.birthdays'), to: { name: 'birthdays' }, icon: Cake },
  { key: 'd-projects', title: t('nav.projects'), to: { name: 'projects' }, icon: FolderKanban },
  { key: 'd-vacations', title: t('nav.vacations'), to: { name: 'vacations' }, icon: TreePalm },
  { key: 'd-documents', title: t('nav.documents'), to: { name: 'documents' }, icon: FileText },
  { key: 'd-onboarding', title: t('nav.onboarding'), to: { name: 'onboarding' }, icon: GraduationCap },
  { key: 'd-emergency', title: t('nav.emergency'), to: { name: 'emergency' }, icon: Siren },
  { key: 'd-infrastructure', title: t('nav.infrastructure'), to: { name: 'infrastructure' }, icon: Waypoints },
  { key: 'd-departments', title: t('nav.adminDepartments'), to: { name: 'admin-departments' }, icon: Building2 },
]);

const LIMIT = 6;

function match(haystack: (string | null | undefined)[], needle: string): boolean {
  if (!needle) return true;
  return haystack.some((h) => (h ?? '').toLowerCase().includes(needle));
}

const groups = computed<PaletteGroup[]>(() => {
  const needle = q.value.trim().toLowerCase();
  const out: PaletteGroup[] = [];

  const people = (employees.value ?? [])
    .filter((e) => match([e.lastName, e.firstName, e.middleName, e.position, e.email, e.department?.name], needle))
    .slice(0, LIMIT)
    .map<PaletteItem>((e) => ({
      key: `p-${e.id}`,
      title: [e.lastName, e.firstName].filter(Boolean).join(' '),
      hint: [e.position, e.department?.name].filter(Boolean).join(' · '),
      to: { name: 'employees', query: { id: e.id } },
      employee: e,
    }));
  if (people.length) out.push({ key: 'people', label: t('palette.people'), items: people });

  const projs = (projects.value ?? [])
    .filter((p) => match([p.name, p.description], needle))
    .slice(0, LIMIT)
    .map<PaletteItem>((p) => ({
      key: `pr-${p.id}`,
      title: p.name,
      hint: p.description ?? undefined,
      to: { name: 'project-detail', params: { id: p.id } },
      icon: FolderKanban,
    }));
  if (projs.length) out.push({ key: 'projects', label: t('palette.projects'), items: projs });

  const num = needle.replace(/^№\s*/, '');
  const asg = (assignments.value ?? [])
    .filter((a) => (num && String(a.number).startsWith(num)) || match([a.title, a.content, a.project?.name], needle))
    .slice(0, LIMIT)
    .map<PaletteItem>((a) => ({
      key: `as-${a.id}`,
      title: `№ ${a.number} · ${a.title}`,
      hint: [t(`assignments.status.${a.status}`), a.project?.name].filter(Boolean).join(' · '),
      to: { name: 'assignment-detail', params: { id: a.id } },
      icon: ClipboardCheck,
    }));
  // Пустой запрос — поручения не показываем: палитра открывается быстрым меню разделов.
  if (needle && asg.length) out.push({ key: 'assignments', label: t('palette.assignments'), items: asg });

  const docs = (documents.value ?? [])
    .filter((d) => match([d.title, d.description, d.category], needle))
    .slice(0, LIMIT)
    .map<PaletteItem>((d) => ({
      key: `doc-${d.id}`,
      title: d.title,
      hint: d.category ?? undefined,
      to: { name: 'documents' },
      icon: FileText,
    }));
  if (docs.length) out.push({ key: 'documents', label: t('palette.documents'), items: docs });

  const dests = destinations.value.filter((d) => match([d.title], needle)).slice(0, LIMIT);
  if (dests.length) out.push({ key: 'actions', label: t('palette.sections'), items: dests });

  return out;
});

/** Плоский список для навигации стрелками. */
const flat = computed<PaletteItem[]>(() => groups.value.flatMap((g) => g.items));

watch(q, () => {
  cursor.value = 0;
});

function indexOf(item: PaletteItem): number {
  return flat.value.findIndex((i) => i.key === item.key);
}

function go(item?: PaletteItem) {
  const target = item ?? flat.value[cursor.value];
  if (!target) return;
  open.value = false;
  void router.push(target.to);
}

function move(delta: number) {
  const n = flat.value.length;
  if (!n) return;
  cursor.value = (cursor.value + delta + n) % n;
  void nextTick(() => {
    listEl.value?.querySelector('.pal-item--sel')?.scrollIntoView({ block: 'nearest' });
  });
}

function onKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    open.value = !open.value;
    return;
  }
  if (!open.value) return;
  if (e.key === 'Escape') {
    e.preventDefault();
    open.value = false;
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    move(1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    move(-1);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    go();
  }
}

watch(open, (v) => {
  if (v) {
    q.value = '';
    cursor.value = 0;
    void nextTick(() => inputEl.value?.focus());
  }
});

onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));
</script>

<template>
  <Teleport to="body">
    <Transition name="pal">
      <div v-if="open" class="pal-overlay" @click.self="open = false">
        <div class="pal" role="dialog" aria-modal="true">
          <div class="pal__input">
            <Search :size="17" :stroke-width="1.75" class="pal__search-icon" />
            <input
              ref="inputEl"
              v-model="q"
              type="text"
              :placeholder="t('palette.placeholder')"
              autocomplete="off"
              spellcheck="false"
            />
            <kbd class="pal__kbd">ESC</kbd>
          </div>

          <div ref="listEl" class="pal__list">
            <template v-for="g in groups" :key="g.key">
              <div class="pal__group">{{ g.label }}</div>
              <button
                v-for="item in g.items"
                :key="item.key"
                type="button"
                class="pal-item"
                :class="{ 'pal-item--sel': indexOf(item) === cursor }"
                @mouseenter="cursor = indexOf(item)"
                @click="go(item)"
              >
                <EmployeeAvatar
                  v-if="item.employee"
                  :employee-id="item.employee.id"
                  :photo-key="item.employee.photoKey"
                  :last-name="item.employee.lastName"
                  :first-name="item.employee.firstName"
                  :size="24"
                />
                <component :is="item.icon"
v-else
:size="17"
:stroke-width="1.75"
class="pal-item__icon" />
                <span class="pal-item__title">{{ item.title }}</span>
                <span v-if="item.hint" class="pal-item__hint">{{ item.hint }}</span>
              </button>
            </template>
            <p v-if="!flat.length" class="pal__empty">{{ t('palette.empty') }}</p>
          </div>

          <div class="pal__foot">
            <span><kbd class="pal__kbd">↑</kbd><kbd class="pal__kbd">↓</kbd> {{ t('palette.hintNav') }}</span>
            <span><kbd class="pal__kbd">↵</kbd> {{ t('palette.hintOpen') }}</span>
            <span><kbd class="pal__kbd">⌘K</kbd> {{ t('palette.hintClose') }}</span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.pal-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  background: rgba(20, 20, 35, 0.42);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 14vh var(--space-4) var(--space-4);
}
.pal {
  width: min(560px, 100%);
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 70vh;
}
.pal__input {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  border-bottom: 1px solid var(--color-border-subtle);
  flex-shrink: 0;
}
.pal__search-icon {
  color: var(--color-text-placeholder);
  flex-shrink: 0;
}
.pal__input input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  font-family: inherit;
  font-size: var(--font-size-md);
  color: var(--color-text);
}
.pal__input input::placeholder {
  color: var(--color-text-placeholder);
}
.pal__kbd {
  font-family: var(--font-family-mono);
  font-size: 10px;
  background: var(--color-bg-soft);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  padding: 2px 5px;
  color: var(--color-text-muted);
}
.pal__list {
  overflow-y: auto;
  padding: var(--space-2);
  flex: 1;
}
.pal__group {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-uppercase);
  color: var(--color-text-muted);
  padding: var(--space-2) var(--space-3) var(--space-1);
}
.pal-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: var(--font-size-base);
  color: var(--color-text);
}
.pal-item--sel {
  background: var(--color-primary-soft);
}
.pal-item__icon {
  color: var(--color-text-muted);
  flex-shrink: 0;
}
.pal-item--sel .pal-item__icon {
  color: var(--color-primary);
}
.pal-item__title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: var(--font-weight-medium);
}
.pal-item__hint {
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 45%;
  flex-shrink: 0;
}
.pal__empty {
  margin: 0;
  padding: var(--space-6);
  text-align: center;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}
.pal__foot {
  display: flex;
  gap: var(--space-4);
  padding: var(--space-2) var(--space-4);
  border-top: 1px solid var(--color-border-subtle);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  flex-shrink: 0;
}

.pal-enter-active,
.pal-leave-active {
  transition: opacity var(--transition-fast);
}
.pal-enter-active .pal,
.pal-leave-active .pal {
  transition: transform var(--transition-fast);
}
.pal-enter-from,
.pal-leave-to {
  opacity: 0;
}
.pal-enter-from .pal,
.pal-leave-to .pal {
  transform: translateY(-6px) scale(0.99);
}

@media (max-width: 720px) {
  .pal-overlay {
    padding: var(--space-4);
    align-items: flex-start;
  }
  .pal__foot {
    display: none;
  }
}
</style>
